import { AppRow } from '../appTable';
import { ensureBoolean } from '../../../../utils';
import {
  BooleanOption,
  DateFilterComparison,
  DateFilterData,
  DateFilterDataSchema,
  NumberFilterComparison,
  NumberFilterData,
  NumberFilterDataSchema,
  OwnerFilterData,
  RecordSearchFilterData,
  SelectionOption,
  isBetweenComparison,
  isNumberBetweenComparison,
} from '../DataTableTypes';
import { BLANK_TABLE_FILTER_KEY } from '../tableUtilsNew';

/**
 * Filter Function for Set Filter
 */
export const setFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: Set<any>,
) => {
  if (!filterValue || !filterValue.size) {
    return true;
  }
  const value = row.getValue<any>(columnId) ?? BLANK_TABLE_FILTER_KEY;
  return filterValue.has(value);
};

/**
 * Filter Function for Selection column filter
 */
export const selectionFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: SelectionOption,
) => {
  if (!filterValue || filterValue === 'ALL') {
    return true;
  }
  const isSelected = row.getIsSelected();
  if (
    (filterValue === 'SELECTED' && isSelected) ||
    (filterValue === 'UNSELECTED' && !isSelected)
  ) {
    return true;
  }
  return false;
};

/**
 * Filter Function for Boolean column filter
 */
export const booleanFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: BooleanOption,
) => {
  if (!filterValue || filterValue === 'ALL') {
    return true;
  }
  const value = ensureBoolean(row.getValue<boolean | null>(columnId));
  if (
    (filterValue === 'CHECKED' && value) ||
    (filterValue === 'UNCHECKED' && !value)
  ) {
    return true;
  }
  return false;
};

/**
 * Filter for DATE filter variant
 * Any datetime value is converted to a date string before comparison
 */
export const dateFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: DateFilterData,
) => {
  const parseResults = DateFilterDataSchema.safeParse(filterValue);
  if (!parseResults.success) {
    return true;
  }
  let value = row.getValue<string | null>(columnId) ?? null;
  const { comparison, firstDate, secondDate } = parseResults.data;

  // Handle Blank / Not Blank first
  switch (comparison) {
    case DateFilterComparison.IS_BLANK:
      return !value;
    case DateFilterComparison.IS_NOT_BLANK:
      return !!value;
    default:
      break;
  }

  if (!firstDate || (isBetweenComparison(comparison) && !secondDate)) {
    return true;
  }

  if (!value) {
    return false;
  }
  // strip time to allow for proper comparison with datetime
  value = value.substring(0, 10);

  switch (comparison) {
    case DateFilterComparison.EQUALS:
      return value === firstDate;
    case DateFilterComparison.GREATER_THAN:
      return value > firstDate;
    case DateFilterComparison.GREATER_THAN_EQUAL:
      return value >= firstDate;
    case DateFilterComparison.LESS_THAN:
      return value < firstDate;
    case DateFilterComparison.LESS_THAN_EQUAL:
      return value <= firstDate;
    case DateFilterComparison.BETWEEN_EXCLUSIVE: {
      if (!secondDate) {
        return true;
      }
      return value > firstDate && value < secondDate;
    }
    case 'BETWEEN_INCLUSIVE': {
      if (!secondDate) {
        return true;
      }
      return value >= firstDate && value <= secondDate;
    }
    default:
      return true;
  }
};

export const textFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: string,
) => {
  if (!filterValue || !filterValue.trim()) {
    return true;
  }
  const value = row.getValue<string | null>(columnId) ?? null;

  if (!value) {
    return false;
  }

  return value.includes(filterValue);
};

/**
 * Filter for NUMBER filter variant
 * Compares numeric values using the selected comparison operator
 */
export const numberFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: NumberFilterData,
) => {
  const parseResults = NumberFilterDataSchema.safeParse(filterValue);
  if (!parseResults.success) {
    return true;
  }
  const rawValue = row.getValue<string | number | null>(columnId) ?? null;
  const { comparison, firstValue, secondValue } = parseResults.data;

  // Handle Blank / Not Blank first
  switch (comparison) {
    case NumberFilterComparison.IS_BLANK:
      return rawValue == null;
    case NumberFilterComparison.IS_NOT_BLANK:
      return rawValue != null;
    default:
      break;
  }

  if (!firstValue || (isNumberBetweenComparison(comparison) && !secondValue)) {
    return true;
  }

  if (rawValue == null) {
    return false;
  }

  const value = Number(rawValue);
  const first = Number(firstValue);
  const second = secondValue ? Number(secondValue) : NaN;

  if (isNaN(value) || isNaN(first)) {
    return true;
  }

  switch (comparison) {
    case NumberFilterComparison.EQUALS:
      return Math.abs(value - first) < Number.EPSILON;
    case NumberFilterComparison.GREATER_THAN:
      return value > first;
    case NumberFilterComparison.GREATER_THAN_EQUAL:
      return value >= first;
    case NumberFilterComparison.LESS_THAN:
      return value < first;
    case NumberFilterComparison.LESS_THAN_EQUAL:
      return value <= first;
    case NumberFilterComparison.BETWEEN_EXCLUSIVE: {
      if (isNaN(second)) {
        return true;
      }
      return value > first && value < second;
    }
    case NumberFilterComparison.BETWEEN_INCLUSIVE: {
      if (isNaN(second)) {
        return true;
      }
      return value >= first && value <= second;
    }
    default:
      return true;
  }
};

/**
 * Filter for OWNER filter variant
 * Matches rows where the column value equals the selected owner ID
 */
export const ownerFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: OwnerFilterData | undefined,
) => {
  if (!filterValue || !filterValue.ownerId) {
    return true;
  }
  const value = row.getValue<string | null>(columnId) ?? null;
  return value === filterValue.ownerId;
};

/**
 * Filter for RECORD_SEARCH filter variant
 * Matches rows where the column value equals the selected record id
 */
export const recordSearchFilterFn = (
  row: AppRow<any>,
  columnId: string,
  filterValue: RecordSearchFilterData | undefined,
) => {
  if (!filterValue || !filterValue.id) {
    return true;
  }
  const value = row.getValue<string | null>(columnId) ?? null;
  return value === filterValue.id;
};
